import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import { X } from 'lucide-react';
import { getVouchers, Voucher } from '../utils/api';
import { VoucherTicket } from './VoucherTicket';
import { useVouchers } from '../context/VoucherContext';
import { toast } from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';

interface VoucherDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  productId?: string;
  onShowAuthModal?: () => void;
}

export const VoucherDrawer: React.FC<VoucherDrawerProps> = ({ isOpen, onClose, productId, onShowAuthModal }) => {
  const [vouchers, setVouchers] = useState<Voucher[]>([]);
  const [loading, setLoading] = useState(true);
  const { appliedVoucher, applyVoucher, removeVoucher } = useVouchers();
  const { isLoggedIn, setShowAuthModal } = useAuth();

  useEffect(() => {
    const html = document.documentElement;
    if (isOpen) {
      html.classList.add('no-scroll');
    } else {
      html.classList.remove('no-scroll');
    }
    return () => html.classList.remove('no-scroll');
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const fetchVouchers = async () => {
      setLoading(true);
      try {
        const allVouchers = await getVouchers();
        const filtered = allVouchers.filter(v => {
          if (v.status !== 'active') return false;
          const now = new Date();
          if (v.start_date && now < new Date(v.start_date)) return false;
          if (v.end_date && now > new Date(v.end_date)) return false;
          
          if (productId) {
            if (!v.conditions?.eligible_product_ids || v.conditions.eligible_product_ids.length === 0) return true;
            return v.conditions.eligible_product_ids.includes(productId);
          }
          
          return true;
        });
        setVouchers(filtered);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchVouchers();
  }, [isOpen, productId]);

  return createPortal(
    <AnimatePresence mode="wait">
      {isOpen && (
        <div className="fixed inset-0 z-[1000000] flex justify-end overflow-hidden">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-ink/60 backdrop-blur-[2px] touch-none"
          />

          {/* Drawer Panel */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 35, stiffness: 350 }}
            className="relative w-full max-w-sm bg-white flex flex-col h-full shadow-2xl"
          >
            {/* Drawer Header */}
            <div className="px-8 py-10 flex items-center justify-between shrink-0 bg-white">
              <div className="space-y-1">
                <h2 className="text-headline text-brand-wine uppercase">Voucher</h2>
                <p className="text-label text-muted">
                  {loading ? 'MEMUAT...' : `${vouchers.length} PROMO TERSEDIA`}
                </p>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-slate-50 rounded-full transition-all group"
              >
                <X size={20} className="text-ink group-hover:rotate-90 transition-transform" />
              </button>
            </div>

            {/* Scrollable Content Area */}
            <div
              className="flex-1 overflow-y-auto px-8 pb-10 custom-scrollbar overscroll-contain bg-white"
              data-lenis-prevent
            >
              <div className="space-y-6 py-2">
                {loading ? (
                  Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="h-32 bg-paper animate-pulse border border-ink/5" />
                  ))
                ) : vouchers.length > 0 ? (
                  vouchers.map((voucher) => (
                    <VoucherTicket
                      key={voucher.id}
                      voucher={voucher}
                      isApplied={appliedVoucher?.id === voucher.id}
                      onApply={(v) => {
                        if (v.conditions?.active_days && Array.isArray(v.conditions.active_days) && v.conditions.active_days.length > 0) {
                          const today = new Date().getDay();
                          if (!v.conditions.active_days.includes(today)) {
                            const dayNames = ['Minggu', 'Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu'];
                            const validDaysStr = v.conditions.active_days.map(d => dayNames[d]).join(', ');
                            toast.error(`Voucher ini hanya berlaku pada hari: ${validDaysStr}`);
                            return;
                          }
                        }
                        const isMemberPending = v.conditions?.registered_only && !isLoggedIn;
                        applyVoucher(v);
                        if (isMemberPending) {
                          toast(`Voucher ${v.code} tersimpan! Masuk akun saat checkout untuk mengaktifkannya.`, {
                            icon: '🔐',
                            style: {
                              background: '#722f38',
                              color: '#fff',
                              borderRadius: '0',
                              fontSize: '10px',
                              fontWeight: 'bold',
                              letterSpacing: '0.1em',
                              textTransform: 'uppercase'
                            }
                          });
                        } else {
                          toast.success(`Voucher ${v.code} terpasang!`, {
                            style: {
                              background: '#722f38',
                              color: '#fff',
                              borderRadius: '0',
                              fontSize: '10px',
                              fontWeight: 'bold',
                              letterSpacing: '0.1em',
                              textTransform: 'uppercase'
                            }
                          });
                        }
                        onClose();
                      }}
                      onShowAuthModal={() => {
                        onClose();
                        if (onShowAuthModal) {
                          onShowAuthModal();
                        } else {
                          setShowAuthModal(true);
                        }
                      }}
                    />
                  ))
                ) : (
                  <div className="text-center py-20">
                    <p className="text-xs text-muted font-bold uppercase tracking-widest">Tidak ada voucher tersedia</p>
                  </div>
                )}
              </div>
            </div>

            {/* Drawer Footer */}
            <div className="p-6 bg-white border-t border-slate-100 shrink-0 flex gap-3">
              {appliedVoucher && (
                <button
                  onClick={() => {
                    removeVoucher();
                    toast.success('Voucher dilepas');
                  }}
                  className="w-1/3 py-4 border border-ink/10 text-ink/60 text-[10px] font-bold uppercase tracking-wider hover:text-red-600 hover:border-red-200 transition-all"
                >
                  Lepas
                </button>
              )}
              <button
                onClick={onClose}
                className="flex-1 py-4 bg-brand-wine text-paper text-[11px] font-black uppercase tracking-widest hover:bg-brand-wine-deep transition-all active:scale-[0.98]"
              >
                Konfirmasi
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
};
